import { FC, useEffect, useMemo, useState } from "react";
import { App, Button, Checkbox, Flex, ModalProps, notification, Spin } from "antd";
import { useTranslation } from "react-i18next";
import {
  PurchaseOrderAdd,
  PurchaseOrderAddItem,
  PurchaseOrderItem,
  PurchaseOrderStateEnum,
  Supplier,
  SupplierLadderPrice,
} from "@/types/purchase";
import DetailModal from "@/components/DetailModal";
import PurchaseForm from "./PurchaseForm";
import useDataDetail from "@/hooks/useDataDetail";
import apis, { OrderItemUpdateKey } from "@/apis/purchase";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import useEditByKey from "../hooks/useEditByKey";
import usePage from "@/layout/hooks/usePage";
import { PageId } from "@/types/page";
import { acceptStateUpdate } from "@/apis/supplier";

interface PurchaseDetailProps extends ModalProps {
  orderItemId: string;
  handleCancel: () => void;
  handleChangeStatus: () => void;
  handleOk: (
    data: PurchaseOrderAdd,
    itemData: PurchaseOrderAddItem,
    itemId: string
  ) => Promise<void>;
  order?: PurchaseOrderItem;
  setRequirementDetail?: (data: any) => void;
  isSupplier?: boolean | undefined;
  btnList?: any;
  canEdit: boolean;
}

export interface RequirementColumnSupplier {
  prices: Array<SupplierLadderPrice>;
  supplier: Supplier;
  id: string;
}

const PurchaseDetail: FC<PurchaseDetailProps> = ({
  orderItemId,
  handleCancel,
  handleOk,
  handleChangeStatus,
  order,
  setRequirementDetail,
  isSupplier,
  btnList,
  canEdit,
  ...props
}) => {
  const { t } = useTranslation();
  const { pageId } = usePage();

  const {
    loading: deLoading,
    fetchDetail: fetch,
    detail: itemDetail,
  } = useDataDetail<PurchaseOrderItem, [string]>({ api: apis.itemFetch });

  const [onDone, doneLoading] = useEditByKey<PurchaseOrderStateEnum>(orderItemId);

  //下单要修改的字段
  const [onPay, payLoading] = useEditByKey<PurchaseOrderStateEnum>(orderItemId);

  const { modal } = App.useApp();

  const [acceptLoading, setAcceptLoading] = useState<boolean>(false);

  // 从订单详情中提取供应商数据，避免调用 supplier/list API
  const supplierData = useMemo(() => {
    const supplier = itemDetail?.order?.supplier || order?.order?.supplier;
    return supplier ? [supplier] : [];
  }, [itemDetail, order]);

  // const { onConfirm: handlePay } = useFrontDelTip<void, [key: OrderItemUpdateKey, value: PurchaseOrderStateEnum, successCallback: () => void]>({
  //   cb: (...args) => {
  //     return onPay(...args).then(() => handleChangeStatus())
  //   },
  //   message: t('common.tip'),
  //   desc: t('purchase.plPlaceOrder'),
  // });
  const { onConfirm: handleDone } = useFrontDelTip<void, [key: OrderItemUpdateKey, value: PurchaseOrderStateEnum, successCallback: () => void]>({
    cb: (...args) => {
      return onDone(...args).then(() => handleChangeStatus())
    },
    message: t('common.tip'),
    desc: t('purchase.plOrderOk')
  });

  useEffect(() => {
    if (orderItemId) {
      fetch(orderItemId);
    }
  }, [orderItemId]);

  const onPayed = () => {
    let checked = false;
    modal.confirm({
      title: t('purchase.confirmOrder'),
      width: 400,
      content: (
        <>
          <Checkbox
            onChange={(e) => { checked = e.target.checked }}
          >
            {t('purchase.helpAcc')}
          </Checkbox>

          {
            (!order?.order.receiver || !order?.order.receiverAddress || !order.order.receiverContact || !order?.order.receiverMobile) && (
              <div style={{ marginTop: 4, color: "orange" }}>{t('sale.attention')} {t('purchase.incompleteRecipientInfo')}</div>
            )}
        </>
      ),
      okButtonProps: { loading: payLoading || acceptLoading },
      onOk: async () => {
        await onPay("orderState", PurchaseOrderStateEnum.PAYED, () => { })
          .then(() => handleChangeStatus());
        if (checked) {
          setAcceptLoading(true);
          acceptStateUpdate(itemDetail?.orderId ?? '', 1)
            .then(() => {
              notification.success({ message: t('purchase.helpAccSuc') });
            })
            .catch(() => { })
            .finally(() => setAcceptLoading(false));
        }
      }
    });
  }

  return (
    <DetailModal
      logBookId={itemDetail?.logBookId || ""}
      modalProps={{
        width: '90vw',
        title: <Flex gap={10}>
          <>{t('common.detail')}</>
          {isSupplier && <span>{order?.number}</span>}
          <Flex gap={10}>
            {(pageId === PageId.PUR_ORDER_PENDING_PAY && !isSupplier) && <Button disabled={!canEdit} type='primary' loading={payLoading} onClick={() => onPayed()}>{t('purchase.placeOrder')}</Button>}
            {(pageId !== PageId.PUR_ORDER_DONE && !isSupplier) && <Button disabled={!canEdit} type='primary' loading={doneLoading} danger onClick={() => handleDone("orderState", PurchaseOrderStateEnum.DONE, () => { })}>{t('purchase.completed')}</Button>}
          </Flex>
        </Flex>,
        onCancel: handleCancel,
        ...props,
      }}
    >
      {deLoading ? (
        <Flex
          style={{ width: "100%", height: "100%" }}
          justify="center"
          align="center"
        >
          <Spin />
        </Flex>
      ) : (
        <>
          <PurchaseForm
            btnList={btnList}
            orderItemId={orderItemId}
            handleCancel={handleCancel}
            handleOk={handleOk}
            data={itemDetail ?? null}
            order={order}
            setRequirementDetail={setRequirementDetail}
            isSupplier={isSupplier}
            canEdit={canEdit}
            supplierData={supplierData}
          />
        </>
      )}
    </DetailModal>
  );
};

export default PurchaseDetail;
